import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Language, TeamListElement, TeamListMinimum } from "../../../../client/api";
import { showTranslated, translate } from "../../../../config/translator";
import { ClavaContext } from "../../../../config/contexts";
import MatchdaySelector, { MatchdaySelectorItem } from "../../../components/MatchdaySelector";
import client, { callClientFunc } from "../../../../client";
import { AS_ENDPOINT, PROD_ENDPOINT } from "../../../../config/constants";
import { getMainLeague } from "../../../../config/utils";

type Option<T> = {
  value: T;
  label: string;
};

const WIDTH_OPTIONS = ["300", "360", "400", "500", "600", "700", "800"];
const DEFAULT_WIDTH = 400;
const DEFAULT_WIDTH_STRING = "400";

const createSelectorItems = <T, >(options: Option<T>[]): MatchdaySelectorItem[] =>
  options.map((opt, index) => ({
    key: index,
    label: opt.label
  }));

const findOptionIndex = <T, >(options: Option<T>[], value: T): number =>
  Math.max(0, options.findIndex(opt => opt.value === value));

const parseWidth = (width: string): number => {
  const parsed = parseInt(width, 10);
  return Number.isNaN(parsed) || parsed <= 0 ? DEFAULT_WIDTH : parsed;
};

const validateWidth = (width: string): string => {
  const parsed = parseWidth(width);
  return String(parsed);
};

const isCustomWidth = (width: string, options: readonly string[]): boolean =>
  !options.includes(width);

const getCustomWidthLabel = (
  currentWidth: string,
  lastCustomWidth: string,
  options: readonly string[]
): string => {
  if (isCustomWidth(currentWidth, options)) {
    return currentWidth;
  }
  if (lastCustomWidth && isCustomWidth(lastCustomWidth, options)) {
    return `Custom (${lastCustomWidth})`;
  }
  return "Custom";
};

const getWidthValueToUse = (
  currentWidth: string,
  lastCustomWidth: string,
  options: readonly string[]
): string => {
  if (isCustomWidth(currentWidth, options)) {
    return currentWidth;
  }
  if (lastCustomWidth && isCustomWidth(lastCustomWidth, options)) {
    return lastCustomWidth;
  }
  return currentWidth;
};

const getTeamIdForWidget = (
  team: TeamListMinimum | TeamListElement,
  widgetType: string
): number => {
  if (widgetType === "calendar_table" || widgetType === "league_matches") {
    const mainLeague = getMainLeague(team.leagues);
    if (mainLeague) {
      return mainLeague.id;
    }
    return team.leagues.length === 0 ? -1 : team.leagues[0].id;
  }
  return team.id;
};

const buildWidgetUrl = (
  widgetType: string,
  teamId: number,
  lang: string | undefined,
  theme: string | undefined,
  endpoint: string
): string => {
  const baseUrl = `https://${endpoint === PROD_ENDPOINT ? "clav.app" : endpoint}/widget/${widgetType}/${teamId}`;
  const params: string[] = [];

  if (lang && lang !== "none") {
    params.push(`lang=${lang}`);
  }
  if (theme && theme !== "light") {
    params.push("theme=dark");
  }

  const queryString = params.length > 0 ? `?${params.join("&")}` : "";
  return `${baseUrl}${queryString}`;
};

const createIframeCode = (
  title: string,
  width: number,
  height: number,
  src: string
): string =>
  `<iframe title="Clava - ${title}" width="${width}" height="${height}" src="${src}" referrerpolicy="strict-origin-when-cross-origin" loading="lazy"></iframe>`;

const calculateHeight = (width: number, multiplier: number): number =>
  width * multiplier;

const WidgetManagement: React.FC<{ team: TeamListMinimum | TeamListElement }> = ({ team }) => {
  const { l } = useContext(ClavaContext);
  const [lang, setLang] = useState<string | undefined>("none");
  const [theme, setTheme] = useState<string | undefined>("light");
  const [width, setWidth] = useState<string>(DEFAULT_WIDTH_STRING);
  const [isEditingCustomWidth, setIsEditingCustomWidth] = useState(false);
  const customWidthInputRef = useRef<HTMLInputElement>(null);
  const [languages, setLanguages] = useState<Language[]>([]);
  const [lastCustomWidth, setLastCustomWidth] = useState<string>(DEFAULT_WIDTH_STRING);

  useEffect(() => {
    callClientFunc(client().getLanguages, false).then(setLanguages);
  }, []);

  useEffect(() => {
    if (isEditingCustomWidth && customWidthInputRef.current) {
      customWidthInputRef.current.focus();
      customWidthInputRef.current.select();
    }
  }, [isEditingCustomWidth]);

  const langOptions = useMemo<Option<string>[]>(() => [
    { value: "none", label: translate("browserLang", l) },
    ...languages.map(la => ({ value: la.locale, label: showTranslated(la.name, l) }))
  ], [l, languages]);

  const langItems = useMemo(() => createSelectorItems(langOptions), [langOptions]);
  const langIndex = useMemo(() => findOptionIndex(langOptions, lang ?? "none"), [langOptions, lang]);

  const themeOptions = useMemo<Option<string>[]>(() => [
    { value: "light", label: translate("themedark", l) },
    { value: "dark", label: translate("themelight", l) }
  ], [l]);

  const themeItems = useMemo(() => createSelectorItems(themeOptions), [themeOptions]);
  const themeIndex = useMemo(() => findOptionIndex(themeOptions, theme ?? "light"), [themeOptions, theme]);

  const widthCustomIndex = WIDTH_OPTIONS.length;
  const widthItems = useMemo<MatchdaySelectorItem[]>(() => {
    const baseItems: MatchdaySelectorItem[] = WIDTH_OPTIONS.map((w, index) => ({
      key: index,
      label: w
    }));
    const customLabel = getCustomWidthLabel(width, lastCustomWidth, WIDTH_OPTIONS);
    baseItems.push({ key: widthCustomIndex, label: customLabel });
    return baseItems;
  }, [width, lastCustomWidth, widthCustomIndex]);

  const widthIndex = useMemo(() => {
    const idx = WIDTH_OPTIONS.findIndex(wv => wv === width);
    return idx >= 0 ? idx : widthCustomIndex;
  }, [width, widthCustomIndex]);

  const endpoint = localStorage.getItem(AS_ENDPOINT) ?? PROD_ENDPOINT;

  const getWidgetUrl = useCallback((widgetType: string): string => {
    const teamOrLeagueId = getTeamIdForWidget(team, widgetType);
    return buildWidgetUrl(widgetType, teamOrLeagueId, lang, theme, endpoint);
  }, [team, endpoint, lang, theme]);

  const w = useMemo(() => parseWidth(width), [width]);
  const hMatch = useMemo(() => calculateHeight(w, Math.min(300, 0.60)), [w]);
  const hMatchPast = useMemo(() => Math.min(400, calculateHeight(w, 0.85)), [w]);
  const hList = useMemo(() => calculateHeight(w, 1.5), [w]);

  const handleLangChange = useCallback((idx: number | undefined) => {
    if (idx !== undefined && langOptions[idx]) {
      setLang(langOptions[idx].value);
    }
  }, [langOptions]);

  const handleThemeChange = useCallback((idx: number | undefined) => {
    if (idx !== undefined && themeOptions[idx]) {
      setTheme(themeOptions[idx].value);
    }
  }, [themeOptions]);

  const handleWidthChange = useCallback((idx: number | undefined) => {
    if (idx === undefined) return;

    if (idx === widthCustomIndex) {
      const valueToUse = getWidthValueToUse(width, lastCustomWidth, WIDTH_OPTIONS);
      setWidth(valueToUse);
      setTimeout(() => {
        setIsEditingCustomWidth(true);
      }, 100);
    } else if (WIDTH_OPTIONS[idx]) {
      setWidth(WIDTH_OPTIONS[idx]);
      setIsEditingCustomWidth(false);
    }
  }, [width, lastCustomWidth, widthCustomIndex]);

  const handleWidthContainerClick = useCallback(() => {
    if (isCustomWidth(width, WIDTH_OPTIONS)) {
      setIsEditingCustomWidth(true);
    }
  }, [width]);

  const handleWidthInputBlur = useCallback(() => {
    const validWidth = validateWidth(width);
    setWidth(validWidth);
    setLastCustomWidth(validWidth);
    setIsEditingCustomWidth(false);
  }, [width]);

  const handleWidthInputKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.currentTarget.blur();
    } else if (e.key === "Escape") {
      const widthToRestore = isCustomWidth(width, WIDTH_OPTIONS) ? lastCustomWidth : width;
      setWidth(widthToRestore);
      setIsEditingCustomWidth(false);
    }
  }, [width, lastCustomWidth]);

  return (
    <div className="my-0 px-0">
      <div
        className="rounded-2xl border border-light-gray dark:border-light-gray-dark bg-bg dark:bg-bg-dark shadow-sm p-4">
        <div className="mr-2 flex flex-row flex-wrap gap-x-4 gap-y-2">
          <div className="flex flex-col gap-1">
            <span className="text-xs text-font-600 dark:text-font-600-dark">{translate("language", l)}</span>
            <MatchdaySelector
              value={langIndex}
              onChange={handleLangChange}
              items={langItems}
              withoutChoose
              transparent
              containerClassName="relative overflow-hidden h-10 inline-flex items-center justify-start rounded-lg bg-white dark:bg-bg-secondary-dark border border-light-gray dark:border-light-gray-dark pl-3 pr-8 min-w-[14rem]"
              className="text-left"
            />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-xs text-font-600 dark:text-font-600-dark">{translate("theme", l)}</span>
            <MatchdaySelector
              value={themeIndex}
              onChange={handleThemeChange}
              items={themeItems}
              withoutChoose
              transparent
              containerClassName="relative overflow-hidden h-10 inline-flex items-center justify-start rounded-lg bg-white dark:bg-bg-secondary-dark border border-light-gray dark:border-light-gray-dark pl-3 pr-8 min-w-[10rem]"
              className="text-left"
            />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-xs text-font-600 dark:text-font-600-dark">{translate("widthWidget", l)}</span>
            {!isEditingCustomWidth ? (
              <MatchdaySelector
                value={widthIndex}
                onChange={handleWidthChange}
                items={widthItems}
                withoutChoose
                transparent
                containerClassName="relative overflow-hidden h-10 inline-flex items-center justify-start rounded-lg bg-white dark:bg-bg-secondary-dark border border-light-gray dark:border-light-gray-dark pl-3 pr-8 min-w-[8rem] cursor-pointer"
                className="text-left"
                onContainerClick={handleWidthContainerClick}
              />
            ) : (
              <div
                className="relative overflow-hidden h-10 inline-flex items-center justify-start rounded-lg bg-white dark:bg-bg-secondary-dark border border-light-gray dark:border-light-gray-dark pl-3 pr-8 min-w-[8rem]">
                <input
                  ref={customWidthInputRef}
                  type="number"
                  value={width}
                  onChange={(e) => setWidth(e.target.value)}
                  onBlur={handleWidthInputBlur}
                  onKeyDown={handleWidthInputKeyDown}
                  className="w-full h-full text-left text-font dark:text-font-dark outline-0 bg-transparent"
                />
              </div>
            )}
          </div>
        </div>
        {[
          { type: "next_match", titleKey: "nextMatch" as const, height: hMatch },
          { type: "last_match", titleKey: "lastMatch" as const, height: hMatchPast },
          { type: "team_matches", titleKey: "teamMatchesWidget" as const, height: hList },
          { type: "league_matches", titleKey: "leagueMatchesWidget" as const, height: hList },
          { type: "calendar_table", titleKey: "table" as const, height: hList, hasOverflow: true }
        ].map(({ type, titleKey, height, hasOverflow }) => {
          const title = translate(titleKey, l);
          const url = getWidgetUrl(type);
          const iframeCode = createIframeCode(title, w, height, url);

          return (
            <div key={type} className="my-2">
              <h3 className="text-base font-semibold mb-4">{title}</h3>
              <div
                className="flex flex-col items-start justify-between bg-bg dark:bg-bg-dark p-3 gap-4 rounded-xl xl:flex-row">
                <div className="mr-4 p-2 rounded-lg bg-primary-10">
                  <span className="font-semibold mr-4 block">{translate("embed", l)}:</span>
                  <span className="url">{iframeCode}</span>
                </div>
                <iframe
                  title={title}
                  height={height}
                  width={w}
                  className={hasOverflow ? "overflow-hidden" : undefined}
                  style={{ minWidth: w, width: w, maxHeight: hasOverflow ? height : undefined, height }}
                  src={url}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default WidgetManagement;
